import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  forwardRef,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { equals } from '@softline/core';
import { UiCoreDirectivesModule } from '../../../directives/ui-core-directives.module';
import { translator } from '../../../i18n/functions/translator.function';
import { I18nModule } from '../../../i18n/i18n.module';
import { L10nModule } from '../../../l10n/l10n.module';
import { UiCorePipesModule } from '../../../pipes/ui-core-pipes.module';
import { SelectOptionBase } from './select-option/select-option-base';
import { SelectOptionHeaderDirective } from './select-option/select-option-header.directive';
import { SelectOptionSeparatorDirective } from './select-option/select-option-separator.directive';
import { SelectOptionDirective } from './select-option/select-option.directive';

@Component({
  selector: 'soft-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UiCorePipesModule,
    UiCoreDirectivesModule,
    I18nModule,
    L10nModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.readonly]': 'readonly()',
  },
})
export class SelectComponent<T = any> implements ControlValueAccessor {
  private onChange: Function = () => {};
  private onTouch: Function = () => {};

  protected t = translator();

  readonly isOpen = signal(false);
  readonly openToTheTop = input(false)

  readonly readonly = input(false);
  readonly placeholder = input<string | null>(null);
  readonly classSelectPanel = input<string>('');
  readonly valueInput = input<T | null>(null, { alias: 'value' });

  readonly options = contentChildren(SelectOptionBase);

  readonly displayedValue = computed(() => {
    const selectedOption = this.selectedOption();

    return (
      selectedOption?.element?.nativeElement?.innerHTML ??
      selectedOption?.value() ??
      this.placeholder()
    );
  });

  readonly value = linkedSignal<T | null>(() => this.valueInput());
  readonly valueChange = output<T>();

  readonly selectedOption = computed(() => {
    return this.options().find(
      (option) => this.isOption(option) && equals(option.value(), this.value())
    ) as SelectOptionDirective<T> | null;
  });

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: T): void {
    this.value.set(obj);
  }

  onClick(): void {
    if (this.readonly()) {
      this.isOpen.set(false);
      return;
    }
    this.isOpen.set(!this.isOpen());
  }

  onSelect(value?: T): void {
    console.log('onSelect: ', value);

    const newValue: T | null = value === undefined || value === null ? null : value;
    this.value.set(newValue);
    this.isOpen.set(false);
    this.onChange(newValue);
    this.onTouch();
    this.valueChange.emit(newValue as T);
  }

  onOutsideClick(): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
    }
    this.onTouch();
  }

  isOption(value: SelectOptionBase): value is SelectOptionDirective<T> {
    return value instanceof SelectOptionDirective;
  }

  isSeparator(value: SelectOptionBase): value is SelectOptionSeparatorDirective {
    return value instanceof SelectOptionSeparatorDirective;
  }

  isHeader(value: SelectOptionBase): value is SelectOptionHeaderDirective {
    return value instanceof SelectOptionHeaderDirective;
  }
}
